import { MAX_TAG_NAME, TAG_COLORS, TAG_COLOR_LABEL, TagBadge, useLoad, type ProDocClient, type Tag, type TagColor, type Verb } from '@fiveox/prodoc-ui'
import {
  Alert,
  Button,
  DataTable,
  EmptyState,
  Input,
  NoMatches,
  PageHeader,
  RadioGroup,
  SearchInput,
  Select,
  Skeleton,
  SkeletonRegion,
  useSearch,
  useToast,
  type ColumnDef,
} from '@fiveox/ui'
import { Plus, Trash2 } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router'

import { useOrg } from '../../components/org-context'
import { TAG } from '../../lib/access'
import { ApiError, errorMessage, prodoc } from '../../lib/api'
import { AboutPopover, DialogFooter, FormDialog, plural, useDraft } from './admin-bits'

/**
 * Admin → Tags (FR-14, §9-17) — the organisation's labels, each with how many
 * documents the reader can see carrying it.
 *
 * A tag is a name and a colour from the kit's tones. A document carries it
 * **by id**, so a **rename** or a **recolour** changes one row and every
 * document shows it at once, untouched (D17). **Merge** moves every document
 * of one tag onto another — each carries the target once — and removes the
 * first. **Delete** takes the tag off every document, archived ones included,
 * one audit line per document; nothing else about the documents changes.
 * Uploaders and reviewers make tags on the fly while tagging; managing the
 * list is `can_update` on `tag` (admin), merging and deleting also
 * `can_delete`.
 *
 * **W27 (FR-64, D82).** The intro is one line with an ⓘ (`AboutPopover`). A
 * row has **Edit** — the name and the colour in one dialog — and **Delete**,
 * not a "…" menu: the kit's menu is drawn inside the table's scrolling box and
 * was cut off. **Delete asks how**, neither chosen at first: *merge into
 * another tag* (the service's merge) or *just remove it* (its delete).
 */
export function TagsPage() {
  const { can } = useOrg()
  return (
    <TagsScreen
      client={prodoc}
      can={can}
      renderLink={(href, children) => (
        <Link to={href} className="text-primary font-medium hover:underline">
          {children}
        </Link>
      )}
    />
  )
}

type Editing = { kind: 'new' } | { kind: 'edit' | 'delete'; tag: Tag } | null

/** The screen itself, over any client — the route hands it the app's. */
export function TagsScreen({
  client,
  can,
  renderLink,
}: {
  client: ProDocClient
  can: (resource: string, verb: Verb) => boolean
  /** A count is a door to the register filtered by the tag. */
  renderLink?: (href: string, children: ReactNode) => ReactNode
}) {
  const { toast } = useToast()
  const tags = useLoad(() => client.tags.list(), [client])
  const list = [...(tags.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }))
  const search = useSearch(list, (t) => [t.name, TAG_COLOR_LABEL[t.color as TagColor]])
  const [editing, setEditing] = useState<Editing>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const canCreate = can(TAG, 'can_create')
  const canUpdate = can(TAG, 'can_update')
  const canDelete = can(TAG, 'can_delete')

  const close = () => {
    setEditing(null)
    setError('')
  }

  const run = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true)
    setError('')
    try {
      await fn()
      await tags.reload()
      close()
      toast(done)
    } catch (e) {
      setError(tagRefusal(e))
    } finally {
      setBusy(false)
    }
  }

  const columns: ColumnDef<Tag>[] = [
    { key: 'name', label: 'Tag', width: 260, render: (t) => <TagBadge tag={t} size="md" /> },
    { key: 'color', label: 'Colour', width: 120, render: (t) => TAG_COLOR_LABEL[t.color as TagColor] ?? t.color },
    {
      key: 'document_count',
      label: 'Documents',
      width: 140,
      align: 'right',
      render: (t) => {
        const n = t.document_count ?? 0
        const text = plural(n, 'document')
        return n > 0 && renderLink ? renderLink(`/documents?tag=${encodeURIComponent(t.id)}`, text) : <span className="text-muted-foreground">{text}</span>
      },
    },
  ]

  const header = (
    <PageHeader
      title="Tags"
      description={
        // W27 (FR-64): one line; the rest behind the ⓘ.
        <span className="inline-flex flex-wrap items-center gap-x-1">
          <span>Labels for documents, across workspaces and folders.</span>
          <AboutPopover label="About tags">
            <p>A tag is a name and a colour. A rename or a new colour shows on every document carrying it at once; no document is changed.</p>
            <p>A merge or a delete is recorded on each document it touches.</p>
            <p>Uploaders and reviewers make tags while tagging; managing this list is the administrator’s.</p>
          </AboutPopover>
        </span>
      }
      actions={
        canCreate && (
          <Button onClick={() => setEditing({ kind: 'new' })}>
            <Plus size={16} aria-hidden />
            New tag
          </Button>
        )
      }
    />
  )

  let body: ReactNode
  if (tags.loading && !tags.data) {
    body = (
      <SkeletonRegion label="Loading the tags" className="space-y-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </SkeletonRegion>
    )
  } else if (tags.error && !tags.data) {
    body = (
      <Alert tone="error">
        Could not load the tags: {tags.error}{' '}
        <Button variant="link" onClick={() => void tags.reload()}>
          Try again
        </Button>
      </Alert>
    )
  } else if (list.length === 0) {
    body = (
      <EmptyState
        title="No tags yet"
        description="Tags label documents across workspaces and folders — a period, a client, an audit. Uploaders make them while tagging; you can make them here too."
        action={
          canCreate && (
            <Button variant="outline" onClick={() => setEditing({ kind: 'new' })}>
              New tag
            </Button>
          )
        }
      />
    )
  } else {
    body = (
      <div className="space-y-3">
        <SearchInput value={search.query} onChange={search.setQuery} label="Find a tag" placeholder="Find a tag…" className="w-64" />
        {search.noMatches ? (
          <NoMatches query={search.query} />
        ) : (
          <DataTable
            columns={columns}
            rows={search.results}
            rowKey={(t) => t.id}
            trailing={{
              header: <span className="sr-only">Actions</span>,
              width: 120,
              // W27 (FR-64): buttons, not a menu the table's scrolling box would cut off.
              cell: (t) => (
                <div className="flex items-center justify-end gap-1">
                  {canUpdate && (
                    <Button variant="ghost" size="sm" onClick={() => setEditing({ kind: 'edit', tag: t })} aria-label={`Edit ${t.name}`}>
                      Edit
                    </Button>
                  )}
                  {canDelete && (
                    <Button variant="ghost" size="sm" onClick={() => setEditing({ kind: 'delete', tag: t })} aria-label={`Delete ${t.name}`} title="Delete">
                      <Trash2 size={16} strokeWidth={1.75} aria-hidden />
                    </Button>
                  )}
                </div>
              ),
            }}
          />
        )}
      </div>
    )
  }

  const target = editing && editing.kind !== 'new' ? editing.tag : null

  return (
    <div className="space-y-6">
      {header}
      {body}

      <TagDialog
        open={editing?.kind === 'new' || editing?.kind === 'edit'}
        mode={editing?.kind === 'edit' ? 'edit' : 'new'}
        tag={target}
        busy={busy}
        error={error}
        onClose={close}
        onSave={(input) => {
          if (editing?.kind === 'new') void run(() => client.tags.create(input), `Made “${input.name}”.`)
          else if (target) {
            // Only what changed, in one PATCH (W27): the name, the colour, or both.
            const changes = { ...(input.name !== target.name ? { name: input.name } : {}), ...(input.color !== target.color ? { color: input.color } : {}) }
            void run(() => client.tags.update(target.id, changes), `Saved “${input.name}”.`)
          }
        }}
      />

      <DeleteDialog
        open={editing?.kind === 'delete'}
        tag={target}
        others={list.filter((t) => t.id !== target?.id)}
        canMerge={canUpdate && canDelete}
        busy={busy}
        error={error}
        onClose={close}
        onMerge={(into) => target && void run(() => client.tags.merge(target.id, into.id), `Merged “${target.name}” into “${into.name}”.`)}
        onRemove={() => target && void run(() => client.tags.remove(target.id), `Deleted “${target.name}”.`)}
      />
    </div>
  )
}

/** The words for a tag refusal. */
export function tagRefusal(e: unknown): string {
  if (e instanceof ApiError && e.reason === 'name_taken') return 'A tag with that name already exists — names are unique whatever their case.'
  return errorMessage(e)
}

/** A tag's name as typed: trimmed, required, within the ceiling. */
export function tagNameError(name: string): string | undefined {
  const trimmed = name.trim()
  if (!trimmed) return 'A tag needs a name.'
  if (trimmed.length > MAX_TAG_NAME) return `Up to ${MAX_TAG_NAME} characters.`
  return undefined
}

function ColourChoice({ value, name, onChange }: { value: TagColor; name: string; onChange: (c: TagColor) => void }) {
  return (
    <RadioGroup
      legend="Colour"
      options={TAG_COLORS.map((c) => ({ value: c, label: <TagBadge tag={{ name: name.trim() || TAG_COLOR_LABEL[c], color: c }} />, description: TAG_COLOR_LABEL[c] }))}
      value={value}
      onChange={(v) => onChange(v as TagColor)}
    />
  )
}

function TagDialog({
  open,
  mode,
  tag,
  busy,
  error,
  onClose,
  onSave,
}: {
  open: boolean
  mode: 'new' | 'edit'
  tag: Tag | null
  busy: boolean
  error: string
  onClose: () => void
  onSave: (input: { name: string; color: TagColor }) => void
}) {
  const [draft, setDraft] = useDraft(open, () => ({ name: tag?.name ?? '', color: (TAG_COLORS.includes(tag?.color as TagColor) ? tag?.color : 'neutral') as TagColor }))
  const [tried, setTried] = useState(false)
  useEffect(() => {
    if (open) setTried(false)
  }, [open])
  const problem = tagNameError(draft.name)
  const unchanged = mode === 'edit' && draft.name.trim() === tag?.name && draft.color === tag?.color
  return (
    <FormDialog
      title={mode === 'new' ? 'New tag' : `Edit “${tag?.name ?? ''}”`}
      open={open}
      error={error}
      onClose={onClose}
      onSubmit={() => {
        setTried(true)
        if (!problem && !unchanged) onSave({ name: draft.name.trim(), color: draft.color })
      }}
    >
      <div className="space-y-5">
        <Input
          label="Name"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          error={tried ? problem : undefined}
          hint={mode === 'edit' ? 'Every document carrying it shows the new name and colour at once; none of them is changed.' : 'Unique in this organisation, whatever its case.'}
          maxLength={MAX_TAG_NAME + 10}
          autoFocus
        />
        <ColourChoice value={draft.color} name={draft.name} onChange={(color) => setDraft({ ...draft, color })} />
      </div>
      <DialogFooter busy={busy} onClose={onClose} submitLabel={mode === 'new' ? 'Make tag' : 'Save'} disabled={unchanged} />
    </FormDialog>
  )
}

type DeleteWay = 'merge' | 'remove'

/**
 * Delete a tag, one of two ways (W27, FR-64) — neither chosen when it opens:
 * **merge** it into another tag (the service's merge: every document carrying
 * it carries the other instead, once) or **just remove it** (the service's
 * delete: it comes off every document, archived ones included). Either way the
 * tag is gone and each document touched records it. Merging needs the right to
 * change tags as well, and another tag to merge into.
 */
function DeleteDialog({
  open,
  tag,
  others,
  canMerge,
  busy,
  error,
  onClose,
  onMerge,
  onRemove,
}: {
  open: boolean
  tag: Tag | null
  others: Tag[]
  canMerge: boolean
  busy: boolean
  error: string
  onClose: () => void
  onMerge: (into: Tag) => void
  onRemove: () => void
}) {
  const [how, setHow] = useDraft<DeleteWay | null>(open, () => null)
  const [into, setInto] = useDraft(open, () => '')
  const chosen = others.find((t) => t.id === into) ?? null
  const mayMerge = canMerge && others.length > 0
  const ready = how === 'remove' || (how === 'merge' && chosen !== null)
  const name = tag?.name ?? ''
  const n = tag?.document_count ?? 0
  const carried = n === 0 ? 'No document you can see carries it.' : n === 1 ? '1 document you can see carries it.' : `${plural(n, 'document')} you can see carry it.`
  const ways = [
    ...(mayMerge
      ? [{ value: 'merge' as const, label: 'Merge into another tag', description: `Every document carrying “${name}” carries the tag you choose instead — once, even where it had both.` }]
      : []),
    { value: 'remove' as const, label: 'Just remove it', description: 'It comes off every document carrying it, archived ones included. Nothing else about them changes.' },
  ]
  return (
    <FormDialog
      title={`Delete “${name}”?`}
      open={open}
      error={error}
      onClose={onClose}
      onSubmit={() => {
        if (how === 'merge' && chosen) onMerge(chosen)
        else if (how === 'remove') onRemove()
      }}
    >
      <div className="space-y-4">
        <p className="text-muted-foreground text-sm">{carried} Either way the tag is gone, and each document it touches records the change.</p>
        <RadioGroup legend="How" options={ways} value={how} onChange={(v) => setHow(v as DeleteWay)} />
        {how === 'merge' && (
          <Select label="Into" value={into} onChange={(e) => setInto(e.target.value)}>
            <option value="">Choose the tag to keep…</option>
            {others.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        )}
      </div>
      <DialogFooter
        busy={busy}
        onClose={onClose}
        tone="danger"
        submitLabel="Delete tag"
        busyLabel="Deleting…"
        disabled={!ready}
        note={how === null ? 'Choose how first.' : how === 'merge' && !chosen ? 'Choose the tag to keep.' : undefined}
      />
    </FormDialog>
  )
}
